import classnames from 'classnames/bind'
import { useState } from 'react'
import { useTranslate } from 'react-polyglot'
import { GlobalThemeColors } from '~/@types/colors'
import { GlobalTextPreset } from '~/@types/text-preset'
import { GlobalTextStyling } from '~/@types/text-styling'

import Tag from '~/components/Abstracts/Tag'
import InlineCta from '~/components/UI/InlineCta'
import { VariantImageSelector } from '~/components/UI/ProductVariantSelector/VariantImageSelector'

import { useProductHeaderProvider } from '~/providers/ProductPageProvider'
import { useStyle } from '~/providers/StyleProvider'

import useLocale from '~/hooks/useLocale'

import { CTA, CUSTOMIZER, PRODUCTS_TYPES } from '~/data/dictionary'
import { SerializedProduct } from '~/data/product-card-data'
import serializePrice from '~/data/serialize-price'

import css from './styles.module.scss'

const cx = classnames.bind(css)
export interface VariantsGridProps {
  className?: string
  title?: string
  currentVariant?: string
  onClick?(customizerProduct, required): void
  product: SerializedProduct
  required?: boolean
  withPriceDifference?: boolean
}

const DEFAULT_DISPLAYED_ITEMS = 18

export default function VariantsGrid({
  className,
  product,
  title,
  required,
  currentVariant,
  onClick,
  withPriceDifference = true,
}: VariantsGridProps) {
  const t = useTranslate()
  const items = product?.variants
  const locale = useLocale()
  const withViewMoreButton = items?.length > DEFAULT_DISPLAYED_ITEMS
  const [isOpen, setOpen] = useState(!withViewMoreButton)
  const { isLoadingPreviewImages } = useProductHeaderProvider()
  const processedItems =
    items?.slice(
      0,
      withViewMoreButton
        ? isOpen
          ? items?.length
          : DEFAULT_DISPLAYED_ITEMS
        : items?.length,
    ) ?? []

  const variant = items?.find((item) => item?.id === currentVariant) ?? null

  const productTitleStyle = useStyle({
    textPreset: GlobalTextPreset.Cta12Primary,
    color: GlobalThemeColors.Black,
    textStyling: GlobalTextStyling.UpperCase,
  })
  const titleStyle = useStyle({
    textPreset: GlobalTextPreset.Label11Secondary,
    color: GlobalThemeColors.Gray1ToBlack,
    textStyling: GlobalTextStyling.UpperCase,
  })
  const nameStyle = useStyle({
    color: GlobalThemeColors.Black,
  })

  const option = variant?.selectedOptions?.[0] ?? null

  const handleOnClick = (id) => {
    onClick(
      { ...product, activeVariant: items?.find((i) => i.id === id) },
      required,
    )
  }

  const priceDifference = `${
    variant?.price?.amount > 0 ? '+' : ''
  }${serializePrice(
    locale,
    variant?.price?.currencyCode,
    variant?.price?.amount,
  )}`

  return (
    <div className={cx(css.VariantsGrid, className)}>
      {title && <div className={productTitleStyle}>{title}</div>}
      <div className={cx(css.bottomContent)}>
        <div className={cx(css.title, titleStyle)}>
          <span className={cx(nameStyle)}>
            {option?.name ?? t(CUSTOMIZER.NO_TYPE(product?.productType))}
          </span>
          {option?.value && <span>{option?.value}</span>}
        </div>

        {withPriceDifference && variant?.price.amount > 0 && (
          <Tag textPreset={GlobalTextPreset.Cta12PrimaryReg}>
            {priceDifference}
          </Tag>
        )}
      </div>
      <div className={cx(css.variantsContainer)}>
        {processedItems?.map((item) => (
          <VariantImageSelector
            key={item?.id}
            className={cx(css.variantImageSelector)}
            active={item?.id === variant?.id}
            item={item}
            ariaLabel={`${t(PRODUCTS_TYPES.TYPE(product.productType))}: ${
              item?.selectedOptions?.[0]?.value
            }`}
            isLoading={isLoadingPreviewImages && item?.id === variant?.id}
            withClose={!required}
            onClick={handleOnClick}
          />
        ))}
      </div>
      {withViewMoreButton && (
        <InlineCta
          className={cx(css.toggleViewMore)}
          onClick={() => setOpen(!isOpen)}
          textPreset={GlobalTextPreset.Cta12PrimaryReg}
          textStyling={GlobalTextStyling.UpperCase}
          theme={GlobalThemeColors.Black}>
          {isOpen ? t(CTA.REDUCE) : t(CTA.VIEW_MORE)}
        </InlineCta>
      )}
    </div>
  )
}
